<script>
import Draggable from 'vuedraggable';
import CustomFieldEditModal from '@app/components/CustomFieldEditModal.vue';

const FIELD_TYPE_LABELS = {
  text: 'Texte court',
  textarea: 'Texte long',
  select: 'Liste déroulante',
  checkbox: 'Case à cocher',
};

export default {
  components: { CustomFieldEditModal, Draggable },
  props: {
    value: {
      type: Object,
      default: () => ({ enabled: false, fields: [] }),
    },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      modalOpenIndex: null,
      isAdding: false,
    };
  },
  computed: {
    options() {
      return this.value || { enabled: false, fields: [] };
    },
    fields() {
      return Array.isArray(this.options.fields) ? this.options.fields : [];
    },
    existingNames() {
      return this.fields.map((f) => f.name);
    },
    editingField() {
      if (this.modalOpenIndex === null) return null;
      return this.fields[this.modalOpenIndex] || null;
    },
  },
  methods: {
    fieldTypeLabel(type) {
      return FIELD_TYPE_LABELS[type] || type;
    },
    onEnabledChange(value) {
      this.emitUpdate({ ...this.options, enabled: value });
    },
    openAdd() {
      this.isAdding = true;
    },
    openEdit(idx) {
      this.modalOpenIndex = idx;
    },
    closeModal() {
      this.modalOpenIndex = null;
      this.isAdding = false;
    },
    handleSave(payload) {
      let next;
      if (this.isAdding) {
        if (this.existingNames.includes(payload.name)) {
          this.$showMessage.error('Un champ avec cet identifiant existe déjà.');
          return;
        }
        next = [...this.fields, payload];
      } else {
        next = this.fields.map((f, i) => (i === this.modalOpenIndex ? { ...payload, name: f.name } : f));
      }
      this.emitUpdate({ ...this.options, fields: next });
      this.closeModal();
    },
    onReorder(newFields) {
      this.emitUpdate({ ...this.options, fields: newFields });
    },
    removeField(idx) {
      this.$buefy.dialog.confirm({
        message: 'Supprimer cette question ?<br><br>Les réponses déjà collectées ne seront plus visibles '
          + 'dans les fiches apprenants et vous n’y aurez plus accès.',
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: () => {
          const next = this.fields.filter((_, i) => i !== idx);
          this.emitUpdate({ ...this.options, fields: next });
        },
      });
    },
    emitUpdate(newOptions) {
      this.$emit('input', newOptions);
      this.$emit('save', newOptions);
    },
  },
};
</script>

<template>
  <div>
    <div class="field">
      <b-switch
        :value="options.enabled"
        @input="onEnabledChange"
      >
        Activer les questions préliminaires
      </b-switch>
    </div>

    <template v-if="options.enabled">
      <hr>

      <div v-if="!fields.length" class="notification is-light">
        Aucune question préliminaire n'a été créée pour le moment.<br/>
        Cliquez sur le bouton "Ajouter une question" ci-dessous.
      </div>

      <draggable
        :value="fields"
        tag="div"
        handle=".cf-handle"
        @input="onReorder"
      >
        <div
          v-for="(field, index) in fields"
          :key="field.name"
          class="box is-flex is-justify-content-space-between is-align-items-center mb-3"
        >
          <div class="is-flex is-align-items-center" style="min-width: 0;">
            <b-icon
              class="cf-handle cursor-grab mr-3 has-text-grey-light"
              icon="grip-lines"
            />
            <div style="cursor: pointer;" @click="openEdit(index)">
              <h4 class="title is-6 mb-1">
                {{ field.label }}
                <span v-if="field.required" class="has-text-danger">*</span>
              </h4>
              <p class="is-size-8 has-text-grey-dark">
                {{ fieldTypeLabel(field.type) }}
                <template v-if="field.type === 'select' && field.options && field.options.length">
                  · {{ field.options.length }} option{{ field.options.length > 1 ? 's' : '' }}
                </template>
              </p>
            </div>
          </div>
          <div @click.stop>
            <b-dropdown position="is-bottom-left" aria-role="menu">
              <template #trigger>
                <b-button type="is-text" icon-left="ellipsis-h" />
              </template>
              <b-dropdown-item aria-role="menuitem" @click="openEdit(index)">
                Modifier
              </b-dropdown-item>
              <b-dropdown-item aria-role="menuitem" @click="removeField(index)">
                <span class="has-text-danger">Supprimer</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </draggable>

      <div class="field has-text-right">
        <b-button
          type="is-primary is-custom"
          icon-left="plus"
          @click="openAdd"
        >
          Ajouter une question
        </b-button>
      </div>
    </template>

    <b-modal
      :active="modalOpenIndex !== null || isAdding"
      has-modal-card
      trap-focus
      @close="closeModal"
    >
      <CustomFieldEditModal
        v-if="modalOpenIndex !== null || isAdding"
        :field="editingField"
        :existing-names="existingNames"
        @save="handleSave"
        @close="closeModal"
      />
    </b-modal>
  </div>
</template>
